/**
 * Axios API client configuration with interceptors for authentication
 */
import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@/types';
import { useLanguageStore } from '@/store/languageStore';
import { getAuthErrorTranslation } from '@/utils/translations';
import { UNAUTHORIZED_EVENT } from './events';

export { UNAUTHORIZED_EVENT };

// API base URL - uses environment variable or falls back to '/api' for dev proxy
const API_BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const TOKEN_KEY = 'access_token';

export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    // 401: the token is missing, expired or revoked. Drop it and tell the auth store, but never
    // navigate: a background request (favorites, progress) must not throw a visitor off a public
    // page. Pages that need a signed-in user show their own sign-in prompt.
    // A 401 for a token that has since been replaced (e.g. a request sent before login finished)
    // says nothing about the current session, so it is ignored.
    const sentAuthorization = error.config?.headers?.Authorization;
    const currentToken = getToken();
    const sentCurrentToken = currentToken === null || sentAuthorization === `Bearer ${currentToken}`;
    if (error.response?.status === 401 && sentCurrentToken) {
      removeToken();
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }

    // Extract error message. A 429 (SEC2 rate limits) gets the translated text, the same one the
    // auth error mapping shows (utils/authErrors.ts, A6).
    const message =
      error.response?.status === 429
        ? getAuthErrorTranslation('tooManyRequests', useLanguageStore.getState().language)
        : error.response?.data?.detail || error.message || 'An error occurred';

    return Promise.reject({
      message,
      status: error.response?.status,
      data: error.response?.data,
    });
  }
);

/** True if a rejection from apiClient is a 429 (rate limited). */
export function isRateLimited(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 429;
}

// Generic request helper with type safety
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.request<T>(config);
  return response.data;
}

// Form data helper for multipart/form-data requests
export function createFormData(data: Record<string, string | Blob>): FormData {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    formData.append(key, value);
  });
  return formData;
}

// URL-encoded form data helper for application/x-www-form-urlencoded
export function createUrlEncodedData(data: Record<string, string>): URLSearchParams {
  return new URLSearchParams(data);
}

export default apiClient;
